import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../css/ResetPassword.css";

/**
 * ENV
 * REACT_APP_API_BASE_URL=https://grab.newedge.bt/driver
 */
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
const API_BASE = `${API_BASE_URL}/api/forgotpassword`;

export default function ResetPassword() {
  const navigate = useNavigate(); // ✅ redirect helper

  // mode: "email" | "phone"
  const [mode, setMode] = useState("email");

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("+975"); // ✅ auto prefix

  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [stage, setStage] = useState("sendOtp"); // sendOtp | verifyOtp | resetPassword
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const identifierLabel = useMemo(
    () => (mode === "email" ? "Email" : "Phone Number"),
    [mode]
  );

  function showAlert(type, message, autoClose = true) {
    setAlert({ type, message });
    if (autoClose && (type === "success" || type === "info")) {
      setTimeout(() => setAlert(null), 4000);
    }
  }

  const handleRequest = async (endpoint, body) => {
    const url = `${API_BASE}${endpoint}`;
    try {
      console.log("Calling API:", url, body);

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const text = await res.text();

      if (text.startsWith("<")) {
        throw new Error(`Received HTML instead of JSON. Check backend / URL: ${url}`);
      }

      let data = {};
      try {
        data = JSON.parse(text || "{}");
      } catch {
        data = {};
      }

      if (!res.ok) {
        throw new Error(data.message || `Request failed (status: ${res.status})`);
      }

      return data;
    } catch (err) {
      console.error("API Error:", err.message);
      throw err;
    }
  };

  // reset stage when switching mode
  useEffect(() => {
    setAlert(null);
    setOtp("");
    setNewPassword("");
    setStage("sendOtp");

    if (mode === "phone") {
      setPhone((prev) => {
        const digits = String(prev || "").replace(/\D/g, "");
        if (digits.startsWith("975")) return `+${digits.slice(0, 11)}`; // 975 + 8 digits
        return "+975";
      });
    }
  }, [mode]);

  const strongPasswordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

  const isValidEmail = (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

  // ✅ normalize and validate Bhutan phone: +975 + 8 digits
  const normalizePhone = (v) => {
    const raw = String(v || "");
    const digits = raw.replace(/\D/g, "");

    let rest = digits;
    if (rest.startsWith("975")) rest = rest.slice(3);
    rest = rest.slice(0, 8);

    return `+975${rest}`;
  };

  const isValidBhPhone = (v) => /^\+975\d{8}$/.test(normalizePhone(v));

  const validateIdentifier = () => {
    if (mode === "email") {
      if (!email.trim()) return "Email is required";
      if (!isValidEmail(email)) return "Enter a valid email";
    } else {
      const normalized = normalizePhone(phone);
      if (normalized === "+975") return "Phone number is required";
      if (!isValidBhPhone(phone)) return "Phone must be like +975XXXXXXXX";
    }
    return null;
  };

  // ====== stage handlers ======
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setAlert(null);

    const errMsg = validateIdentifier();
    if (errMsg) return showAlert("danger", errMsg, false);

    setLoading(true);
    try {
      if (mode === "email") {
        await handleRequest("/send-otp", { email: email.trim() });
        showAlert("success", "OTP sent to your email");
      } else {
        const normalized = normalizePhone(phone);
        await handleRequest("/send-otp-sms", { phone: normalized });
        showAlert("success", "OTP sent to your phone");
      }
      setStage("verifyOtp");
    } catch (err) {
      showAlert("danger", err.message, false);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setAlert(null);

    if (!otp.trim() || otp.trim().length !== 6) {
      return showAlert("danger", "Enter a valid 6-digit OTP", false);
    }

    setLoading(true);
    try {
      if (mode === "email") {
        await handleRequest("/verify-otp", { email: email.trim(), otp: Number(otp) });
        showAlert("success", "OTP verified! Set a new password.");
      } else {
        const normalized = normalizePhone(phone);
        await handleRequest("/verify-otp-sms", { phone: normalized, otp: otp.trim() });
        showAlert("success", "OTP verified! Set a new password.");
      }
      setStage("resetPassword");
    } catch (err) {
      showAlert("danger", err.message, false);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setAlert(null);

    if (!newPassword) return showAlert("danger", "Enter your new password", false);

    if (!strongPasswordRegex.test(newPassword)) {
      return showAlert(
        "danger",
        "Password must include uppercase, lowercase, number, and special character (8+ chars)",
        false
      );
    }

    setLoading(true);
    try {
      if (mode === "email") {
        await handleRequest("/reset-password", { email: email.trim(), newPassword });
      } else {
        const normalized = normalizePhone(phone);
        await handleRequest("/reset-password-sms", { phone: normalized, newPassword });
      }

      showAlert("success", "Password reset successful! Redirecting...");

      // ✅ redirect to login after a short delay
      setTimeout(() => {
        navigate("/", { replace: true }); // if your login route is different, change "/"
      }, 1200);
    } catch (err) {
      showAlert("danger", err.message, false);
    } finally {
      setLoading(false);
    }
  };

  const shownIdentifier = mode === "email" ? email : normalizePhone(phone);

  const handlePhoneChange = (val) => {
    setPhone(normalizePhone(val));
  };

  return (
    <div className="login-container">
      {alert && (
        <div className={`custom-alert alert-${alert.type} show`} role="alert">
          <span className="alert-icon">
            {alert.type === "danger" && <i className="bi bi-exclamation-triangle-fill"></i>}
            {alert.type === "success" && <i className="bi bi-check-circle-fill"></i>}
            {alert.type === "info" && <i className="bi bi-info-circle-fill"></i>}
          </span>
          <span>{alert.message}</span>
          <button className="alert-close" onClick={() => setAlert(null)}>
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}

      <div className="card card-authentication fade-in">
        <div className="card-header">
          <img src="/assets/img/loginlogo.png" className="logo-icon" alt="Logo" />
          <h2 className="card-title">Reset Password</h2>
        </div>

        <div className="card-body">
          {/* Step indicator */}
          <div className="step-indicator">
            <div className={`step ${stage !== "sendOtp" ? "completed" : "active"}`}>
              <div className="step-num">
                {stage !== "sendOtp" ? <i className="bi bi-check-lg" /> : "1"}
              </div>
              <span className="step-label">Request</span>
            </div>
            <div className={`step-line ${stage !== "sendOtp" ? "completed" : ""}`} />
            <div className={`step ${stage === "verifyOtp" ? "active" : stage === "resetPassword" ? "completed" : ""}`}>
              <div className="step-num">
                {stage === "resetPassword" ? <i className="bi bi-check-lg" /> : "2"}
              </div>
              <span className="step-label">Verify</span>
            </div>
            <div className={`step-line ${stage === "resetPassword" ? "completed" : ""}`} />
            <div className={`step ${stage === "resetPassword" ? "active" : ""}`}>
              <div className="step-num">3</div>
              <span className="step-label">Reset</span>
            </div>
          </div>

          {/* Mode selector */}
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="reset-using-label">
              Reset using
            </label>
            <div style={{ position: "relative" }}>
              <select
                className="form-control"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                disabled={loading}
                style={{ appearance: "auto" }}
              >
                <option value="email">Enter Email</option>
                <option value="phone">Enter Phone Number</option>
              </select>
              <i className="bi bi-chevron-down input-icon" style={{ pointerEvents: "none" }}></i>
            </div>
          </div>

          {stage === "sendOtp" && (
            <form onSubmit={handleSendOtp}>
              <p>Enter your registered {identifierLabel.toLowerCase()} to receive OTP.</p>

              {mode === "email" ? (
                <div className="form-group">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                  />
                  <i className="bi bi-envelope-fill input-icon"></i>
                </div>
              ) : (
                <div className="form-group">
                  <input
                    type="tel"
                    inputMode="numeric"
                    className="form-control"
                    placeholder="+975XXXXXXXX"
                    value={normalizePhone(phone)}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    disabled={loading}
                    required
                  />
                  <i className="bi bi-telephone-fill input-icon"></i>
                </div>
              )}

              <button type="submit" className="btn btn-signin-like" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : "Send OTP"}
              </button>
            </form>
          )}

          {stage === "verifyOtp" && (
            <form onSubmit={handleVerifyOtp}>
              <p>
                Enter the 6-digit OTP sent to <strong>{shownIdentifier}</strong>
              </p>

              <div className="otp-boxes">
                {Array.from({ length: 6 }).map((_, i) => (
                  <input
                    key={i}
                    id={`otp-box-${i}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className={`otp-box${otp[i] ? " filled" : ""}`}
                    value={otp[i] || ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 1);
                      const arr = otp.padEnd(6, " ").split("");
                      arr[i] = val || " ";
                      setOtp(arr.join("").trimEnd().replace(/ /g, ""));
                      if (val && i < 5) document.getElementById(`otp-box-${i + 1}`)?.focus();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" && !otp[i] && i > 0) {
                        document.getElementById(`otp-box-${i - 1}`)?.focus();
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
                      setOtp(pasted);
                      const nextIdx = Math.min(pasted.length, 5);
                      document.getElementById(`otp-box-${nextIdx}`)?.focus();
                    }}
                    disabled={loading}
                  />
                ))}
              </div>

              <button type="submit" className="btn btn-signin-like" disabled={loading || otp.replace(/\s/g, "").length < 6}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : "Verify OTP"}
              </button>
            </form>
          )}

          {stage === "resetPassword" && (
            <form onSubmit={handleResetPassword}>
              <p>Set a strong new password.</p>

              <div className="form-group">
                <input
                  type="password"
                  className="form-control"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  required
                />
                <i className="bi bi-lock-fill input-icon"></i>
              </div>

              <button type="submit" className="btn btn-signin-like" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : "Reset Password"}
              </button>
            </form>
          )}

          <div className="back-to-login">
            <Link to="/">← Back to Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
